import React from 'react';
import { useParams, Link } from 'react-router-dom';

const IndustryDetail = () => {
  const { industryName } = useParams();

  const industryData = {
    finance: {
      title: "Finance",
      subtitle: "AI Engineering & Custom Solutions",
      hero: "Helping financial institutions design, build, and deploy AI systems that align with risk, compliance, and operational realities.",
      image: "/images/finance.png",
      overview: "Financial services operate with tight regulatory oversight, complex product portfolios, and large volumes of time-sensitive data. Risk, compliance, fraud, and finance operations teams are expected to move quickly while maintaining high standards of accuracy and auditability. In this environment, generic AI products rarely fit neatly. Each institution has its own policies, data models, workflows, and approval paths. ViconAI works as an engineering and execution partner, helping financial organizations turn AI ideas into production systems that respect those constraints. We focus on practical systems that plug into your existing landscape—core banking, CRM, risk engines, document management, and data platforms—so value appears inside the tools your teams already use.",
      solutions: [
        {
          title: "Risk & Compliance Decision Support",
          description: "AI systems that surface relevant data, patterns, and recommendations to risk and compliance teams while keeping final judgement human-led and auditable."
        },
        {
          title: "Fraud & Anomaly Detection Pipelines",
          description: "Custom models and workflows that analyse transactional and behavioural data to flag unusual activity, prioritise alerts, and reduce manual triage."
        },
        {
          title: "Document & Onboarding Intelligence",
          description: "AI assistance for KYC/AML checks, credit files, and contract review—extracting key fields, highlighting inconsistencies, and supporting review teams."
        },
        {
          title: "Finance Operations Automation",
          description: "AI components that support reconciliations, exception handling, forecasting, and reporting, designed to coexist with existing finance systems."
        }
      ],
      howWeWork: "Our work in finance usually starts with a focused use-case in risk, compliance, fraud, or operations. We collaborate with stakeholders to understand policies, approval chains, and existing systems. We then design solutions that match your governance model: data never leaves approved environments, access controls mirror existing roles, and decisions remain explainable. Prototypes are evaluated on both analytical performance and control-framework fit. When moving to production, we integrate with core systems, audit trails, model risk management processes, and existing reporting, so the AI system becomes a natural part of day-to-day oversight rather than a side tool.",
      valueAdd: [
        "Teams are overloaded with manual reviews, investigations, or reconciliations.",
        "There is rich historical data, but limited ability to turn it into timely risk insight.",
        "Regulators and internal audit demand more transparency and consistency.",
        "Existing tools generate many alerts but low true-positive rates."
      ],
      benefits: [
        "More focused attention from specialist teams on the highest-risk items.",
        "Greater transparency in how data and models inform risk judgements.",
        "Reduced manual effort on repetitive, rules-only checks.",
        "AI initiatives that fit cleanly into existing governance and oversight structures."
      ]
    },

    healthcare: {
      title: "Healthcare",
      subtitle: "AI Systems for Care & Operations",
      hero: "Partnering with healthcare organizations to build responsible AI systems that support clinicians and operations.",
      image: "/images/healthcare.png",
      overview: "Healthcare combines sensitive data, life-critical decisions, and highly constrained workflows. Any AI system must fit around clinicians, nurses, and operational staff—not the other way around—and respect strict privacy and regulatory requirements. ViconAI works with healthcare providers, health-tech companies, and research groups to design AI systems that assist with insight, coordination, and efficiency. Our emphasis is on decision support and workflow enablement, not automation that bypasses clinical judgement. We focus equally on technical robustness and practical adoption: if a system does not work inside the reality of busy clinics or hospitals, it does not create value regardless of model accuracy.",
      solutions: [
        {
          title: "Clinical Decision Support & Triage Assistance",
          description: "AI tools that help surface relevant history, guidelines, and risk indicators to clinicians and triage teams, embedded into existing EHR or clinical systems."
        },
        {
          title: "Operational & Capacity Intelligence",
          description: "Systems that help forecast demand, support bed and theatre scheduling, and highlight operational bottlenecks across departments."
        },
        {
          title: "Medical Imaging & Data Workflows",
          description: "Pipelines that prepare, route, and annotate imaging and structured data for use with approved AI models, including quality checks and audit trails."
        },
        {
          title: "Administrative Automation",
          description: "AI components that assist with documentation, coding, referral management, and other repetitive administrative tasks."
        }
      ],
      howWeWork: "In healthcare, we begin by mapping the clinical or operational workflow in detail: who is involved, what systems they use, what decisions they make, and what constraints they face. We then identify points where AI can provide timely, reliable assistance without adding cognitive load. Designs are validated with clinicians and operations leaders early, often using low-fidelity prototypes before any model work. During implementation, we emphasise integration with existing EHRs, practice-management tools, and governance processes. We work with security and compliance teams to ensure that deployments meet privacy, audit, and safety expectations, and we design clear escalation paths for cases where AI is uncertain or not applicable.",
      valueAdd: [
        "Clinicians and staff are spending significant time navigating records or systems to assemble a complete view of a patient or case.",
        "Operational teams struggle with visibility into demand, capacity, and bottlenecks.",
        "There is interest in AI, but concern about safety, explainability, and adoption.",
        "Existing pilots have not moved into routine clinical or operational use."
      ],
      benefits: [
        "Better-timed and more contextual information for clinical and operational decisions.",
        "Reduced administrative and coordination burden on staff.",
        "AI systems that are designed around real clinical workflows and safety practices.",
        "Stronger confidence from governance and compliance teams in how AI is used."
      ]
    },

    manufacturing: {
      title: "Manufacturing",
      subtitle: "AI for Intelligent Production",
      hero: "Working with manufacturing teams to build AI systems that see, predict, and optimise on the shop floor.",
      image: "/images/manufacturing.png",
      overview: "Manufacturing environments blend physical equipment, production lines, quality processes, and supply chain dependencies. Data comes from machines, sensors, MES/SCADA systems, and ERP platforms—often in different formats and levels of cleanliness. ViconAI helps manufacturing organisations turn this mix into AI systems that support quality, uptime, and planning. We focus on solutions that can tolerate real-world variability: changing lighting, wear and tear, new product variants, and evolving production targets. Our work sits at the intersection of OT and IT. We design systems that are aware of safety constraints, line-change realities, and operator workflows, rather than assuming a lab-like environment.",
      solutions: [
        {
          title: "Vision-based Quality Inspection",
          description: "Computer-vision systems tailored to your specific products, line setups, and defect profiles, with clear integration into rework and approval flows."
        },
        {
          title: "Predictive Maintenance & Asset Health",
          description: "Models that analyse sensor data and maintenance logs to highlight equipment at risk of failure, giving planners more time to act."
        },
        {
          title: "Production Planning & Throughput Intelligence",
          description: "AI tools that support planners in balancing demand, capacity, changeovers, and constraints across lines or plants."
        },
        {
          title: "Safety & Compliance Monitoring Assistance",
          description: "Systems that help monitor adherence to safety processes and capture relevant data for audits and continuous improvement."
        }
      ],
      howWeWork: "We usually begin with a plant walk-through and data discovery: understanding equipment, sensors, line configurations, and existing MES/SCADA and ERP systems. From there, we co-design solutions with production, maintenance, and quality teams, choosing pilot areas where data is available and value is clear. Early prototypes are tested on historical and live data to validate feasibility, operator experience, and integration points. When moving to production, we pay attention to hardware constraints, network realities, and maintenance workflows. We design deployment and monitoring approaches that fit within existing engineering and reliability practices instead of creating parallel systems that are hard to sustain.",
      valueAdd: [
        "Quality checks are manual, slow, or inconsistent across shifts or sites.",
        "Unplanned downtime has high cost and current maintenance is heavily reactive.",
        "Planners struggle to reconcile changing demand with real line capacity.",
        "There is data from machines and sensors, but limited use beyond dashboards."
      ],
      benefits: [
        "More consistent quality decisions with less manual inspection overhead.",
        "Greater visibility into emerging equipment risks before failures occur.",
        "Planning decisions supported by better insight into true capacity and constraints.",
        "AI initiatives that respect safety, reliability, and engineering practices."
      ]
    },

    automotive: {
      title: "Automotive",
      subtitle: "AI for Mobility & Industrial Systems",
      hero: "Helping automotive organisations build AI systems across manufacturing, fleets, and emerging mobility services.",
      image: "/images/automotive.png",
      overview: "Automotive companies work across long supply chains, complex manufacturing, connected vehicles, and emerging mobility services. Safety and reliability requirements are stringent, and any AI system must operate within regulatory and brand expectations. ViconAI supports automotive organisations in building AI systems that enhance quality, uptime, and operational awareness—on the factory floor, across fleets, and in supporting systems. We prioritise explainability, testing, and staged rollouts. Our role is to partner with engineering, operations, and digital teams to ensure that AI remains a controlled, well-governed component within larger automotive systems.",
      solutions: [
        {
          title: "Manufacturing Quality & Process Analytics",
          description: "AI and vision systems that monitor assembly steps, detect anomalies, and provide feedback to operators and engineers."
        },
        {
          title: "Fleet Health & Maintenance Intelligence",
          description: "Systems that analyse telematics and service data to support maintenance planning and warranty insight for vehicles or equipment."
        },
        {
          title: "In-house Tools for Engineering & Testing",
          description: "AI-assisted tools that help teams explore logs, scenarios, and test data more efficiently during development and validation."
        },
        {
          title: "Operational Support for Mobility Services",
          description: "AI systems that assist with dispatch, demand forecasting, and customer support for mobility or logistics offerings."
        }
      ],
      howWeWork: "In automotive work, we typically collaborate with both operational and engineering stakeholders. We start by identifying where AI can support safety-critical or operational decisions without undermining existing processes. We design systems with clear boundaries and escalation paths: what the AI is allowed to do, what it is not allowed to do, and how humans remain in control. Testing plans are developed alongside implementation, with staged pilots across selected lines, fleets, or regions. Integration with existing toolchains—plant systems, telematics platforms, engineering data stores—is planned early so that AI augments current capabilities rather than competing with them.",
      valueAdd: [
        "Large volumes of sensor, test, or telematics data are under-used.",
        "Quality or reliability issues are costly and root-cause analysis is slow.",
        "New mobility offerings need better forecasting and operational tools.",
        "Teams are exploring AI but want strong guardrails around safety and brand risk."
      ],
      benefits: [
        "Faster and more consistent insight from manufacturing and fleet data.",
        "Better-informed maintenance and quality decisions.",
        "AI systems that are implemented with safety and governance at the forefront.",
        "Stronger internal confidence in expanding AI into additional use-cases."
      ]
    },

    energy: {
      title: "Energy & Utilities",
      subtitle: "AI for Infrastructure and Operations",
      hero: "Working with energy and utility organisations to build AI systems that support reliable, efficient service.",
      image: "/images/energy.png",
      overview: "Energy and utility companies operate critical infrastructure where reliability, safety, and regulatory compliance are non-negotiable. Data is generated across grids, assets, meters, control rooms, and field operations, but turning it into timely, actionable insight is often challenging. ViconAI partners with energy and utility teams to design AI systems that help monitor assets, anticipate issues, and support planning. We design for resilience: systems must behave predictably under stress and integrate with existing SCADA, outage-management, and asset-management tools. Our focus is on augmenting operators and engineers with better visibility and recommendations while keeping control firmly in human hands.",
      solutions: [
        {
          title: "Grid & Network Monitoring Intelligence",
          description: "AI systems that analyse network and asset data to highlight anomalies, potential overload, or instability for operator review."
        },
        {
          title: "Predictive Asset & Maintenance Insights",
          description: "Models that support maintenance scheduling and asset planning by identifying patterns that precede failures."
        },
        {
          title: "Field Operations & Inspection Assistance",
          description: "Vision and data-driven tools that assist teams in identifying issues in field assets such as poles, lines, or substations."
        },
        {
          title: "Planning & Scenario Support",
          description: "AI components that help planners explore demand, generation, and outage scenarios to support long-term decisions."
        }
      ],
      howWeWork: "We start with a joint view of the asset and data landscape: what telemetry is available, how it is stored, and which systems operators rely on today. From there, we co-design AI systems that respect operational procedures and regulatory requirements. Prototypes are often evaluated alongside existing dashboards and tools to ensure they add clarity rather than noise. Production deployments are integrated into existing control-room tools, asset-management platforms, and reporting processes, with clear governance around model changes and incident handling.",
      valueAdd: [
        "Maintenance and outage teams are under pressure to do more with limited resources.",
        "Existing monitoring tools raise many alerts but provide limited prioritisation.",
        "There is interest in using data more effectively but concern about operational risk.",
        "Field inspection and reporting processes are manual and time-consuming."
      ],
      benefits: [
        "Earlier visibility into emerging asset and network issues.",
        "Better prioritisation of maintenance and inspection activities.",
        "Improved confidence that AI systems align with operational and regulatory expectations.",
        "A foundation for extending AI into additional planning and optimisation scenarios."
      ]
    },

    transportation: {
      title: "Transportation",
      subtitle: "AI for Traffic and Mobility Operations",
      hero: "Helping transport authorities and operators build AI systems for safer, more efficient movement.",
      image: "/images/transportation.jpeg",
      overview: "Transport authorities, city agencies, and operators manage growing traffic volumes, safety expectations, and infrastructure constraints. Video feeds, sensors, and operational data are plentiful, but humans alone cannot watch and interpret everything in real time. ViconAI helps transportation stakeholders design AI systems—often vision-driven—that support situational awareness, incident response, and planning. We recognise that decisions impact the public directly, so transparency, robustness, and clear roles for humans are essential. Our work focuses on systems that assist operators and planners, not unbounded automation.",
      solutions: [
        {
          title: "Traffic Monitoring & Event Detection",
          description: "Vision and data systems that help detect congestion, incidents, and abnormal patterns on roads or at junctions."
        },
        {
          title: "Safety & Hazard Awareness",
          description: "AI assistance in identifying potential hazards such as stopped vehicles, wrong-way movement, or infrastructure issues."
        },
        {
          title: "Planning & Policy Support Dashboards",
          description: "Tools that aggregate historical and real-time data to support decisions about signal timing, road design, and interventions."
        },
        {
          title: "Operator Assistance & Workflow Tools",
          description: "Interfaces that bring relevant alerts, video, and context together for control-room teams."
        }
      ],
      howWeWork: "Engagements typically begin with understanding current control-room workflows, data sources, and pain points. We map how incidents are detected today, how information flows, and where delays or blind spots occur. We then co-design AI systems that fit into existing roles: who receives which alert, through which channel, and what actions they can take. Prototypes are tested with real operators using recorded data before any live deployment. In production, we monitor both model performance and operator experience, adjusting thresholds, views, and escalation paths to ensure that AI assistance remains helpful rather than overwhelming.",
      valueAdd: [
        "Control-room teams are overloaded with screens, feeds, and manual monitoring.",
        "Incident detection is reactive and heavily dependent on phone calls or reports.",
        "There is rich video and sensor data that is not fully exploited.",
        "Authorities want to improve safety and flow without expanding infrastructure dramatically."
      ],
      benefits: [
        "Faster and more consistent awareness of incidents and abnormal conditions.",
        "Better support for planning interventions and communicating their impact.",
        "AI systems that operators see as useful partners, not black boxes.",
        "A scalable foundation for expanding AI across additional corridors or modes."
      ]
    },

    telecommunications: {
      title: "Telecommunications",
      subtitle: "AI for Network and Service Operations",
      hero: "Partnering with telecom providers to build AI systems that enhance network visibility and customer experience.",
      image: "/images/telecommunication.png",
      overview: "Telecom networks generate continuous streams of telemetry, alarms, and customer data. Operations, engineering, and customer-facing teams must keep services reliable while handling complexity across technologies, vendors, and geographies. ViconAI works with telecom organisations to design AI systems that support network performance, fault management, and customer operations. We focus on combining domain understanding with data-driven insight so that teams can act earlier and with more context. Our goal is not to replace NOC, SOC, or care teams, but to give them better tools to see patterns, prioritise work, and understand impact.",
      solutions: [
        {
          title: "Network Performance & Anomaly Intelligence",
          description: "AI systems that analyse metrics, alarms, and topology to highlight unusual behaviour and potential degradation across the network."
        },
        {
          title: "Fault Correlation & Ticket Assistance",
          description: "AI support for correlating alarms, suggesting probable root causes, and assisting with ticket enrichment for NOC teams."
        },
        {
          title: "Customer Experience & Care Insights",
          description: "Systems that bring together network, device, and account data to help care agents and digital channels respond more effectively."
        },
        {
          title: "Operational Analytics for Planning",
          description: "AI tools that provide insight into capacity, utilisation, and chronic issues to support engineering and investment decisions."
        }
      ],
      howWeWork: "We usually start with a joint review of network data sources, alarm models, ticketing systems, and care processes. Together with NOC, engineering, and care leaders, we identify use-cases where better correlation or prediction would make a tangible difference. We build and validate models using historical outages, performance data, and ticket histories, ensuring that proposed insights are understandable and actionable by operations teams. For deployment, we integrate into existing consoles, ticketing tools, and dashboards, so that AI assistance appears inside familiar workflows, complemented by monitoring and feedback loops that allow continuous refinement.",
      valueAdd: [
        "Operations teams are flooded with alarms and tickets, many of which are symptoms of the same underlying issue.",
        "Chronic performance or experience issues are hard to quantify and localise.",
        "Care agents lack unified, real-time views of customer and network context.",
        "There is a desire to move from reactive to more proactive network operations."
      ],
      benefits: [
        "More focused operations effort on the issues that matter most.",
        "Faster, better-informed incident investigation and resolution.",
        "Improved support experiences backed by clearer technical insight.",
        "AI systems that align with telecom operations practices and constraints."
      ]
    },

    education: {
      title: "Education",
      subtitle: "AI Systems for Learning and Administration",
      hero: "Supporting education providers in building thoughtful AI systems that respect learners and educators.",
      image: "/images/education.png",
      overview: "Educational institutions and platforms manage diverse learners, evolving curricula, and administrative responsibilities. There is growing interest in using AI to enhance learning and reduce administrative burden—but also concern about privacy, fairness, and the role of educators. ViconAI collaborates with schools, universities, and ed-tech providers to design AI systems that support, rather than replace, teaching and learning. We emphasise transparency, control, and alignment with pedagogical goals. Our work focuses on tools that give educators and administrators better insight and support while keeping final decisions about teaching, grading, and interventions with humans.",
      solutions: [
        {
          title: "Learning Analytics & Engagement Insights",
          description: "AI systems that help educators understand participation, progress, and potential risk of disengagement across cohorts."
        },
        {
          title: "Personalised Support Tools",
          description: "Assistive systems that suggest resources or practice tasks tailored to a learner's needs, under educator oversight."
        },
        {
          title: "Assessment & Feedback Assistance",
          description: "AI components that help with drafting feedback, organising submissions, and highlighting patterns—not automated grading in isolation."
        },
        {
          title: "Administrative Workflow Automation",
          description: "Tools that support scheduling, communication, and routine data tasks for administrative staff."
        }
      ],
      howWeWork: "Engagements in education begin with understanding institutional priorities, current digital platforms, and sensitivities around data and assessment. We work with educators and administrators to identify where AI can responsibly assist—often starting with analytics, content organisation, or administrative relief—before considering more direct learner-facing applications. Design and implementation are done with clear guardrails: what AI is allowed to suggest or automate, how educator control is maintained, and how students' data and rights are protected. Pilot phases include feedback from both staff and learners before broader deployment.",
      valueAdd: [
        "Staff are overwhelmed with administrative tasks that reduce time for teaching and mentoring.",
        "There is limited visibility into which learners may be struggling until late in the term.",
        "Institutions want to explore AI but are cautious about fairness and over-automation.",
        "Existing platforms generate data but not actionable insight."
      ],
      benefits: [
        "Better visibility for educators into learner engagement and needs.",
        "Reduced administrative overhead, freeing time for higher-value work.",
        "AI tools that fit within institutional values and policies.",
        "A pragmatic foundation for extending AI responsibly into teaching and learning."
      ]
    }
  };

  const industry = industryData[industryName];

  if (!industry) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center bg-white">
        <h1 className="text-4xl font-black text-[#17091a] mb-6">Industry Not Found</h1>
        <Link to="/industries" className="text-[#952877] font-bold hover:underline">Return to Industries</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white selection:bg-[#952877]/10 selection:text-[#952877]">
      
      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Decorative Blurs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute top-20 left-0 w-[300px] h-[300px] bg-[#592aa7]/5 blur-[100px] rounded-full pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-block px-4 py-1.5 mb-6 rounded-full border border-[#952877]/20 bg-[#952877]/5">
             <span className="text-[10px] font-black tracking-[0.2em] uppercase text-[#952877]">Specialized Sector</span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black text-[#17091a] mb-6 tracking-tighter leading-none">
            {industry.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto font-light leading-relaxed">
            {industry.hero}
          </p>
        </div>
      </section>

      {/* --- OVERVIEW & LIQUID GLASS STATS --- */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <h2 className="text-xs font-black uppercase tracking-[0.3em] text-[#952877]">Strategic Overview</h2>
            <p className="text-lg text-gray-600 leading-[1.8] font-medium">
              {industry.overview}
            </p>
          </div>
          
          {/* Liquid Glass Benefit Card */}
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-r from-[#952877]/20 to-[#592aa7]/20 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
            <div className="relative bg-white/40 backdrop-blur-xl border border-white/60 rounded-[3rem] p-10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)]">
              <h3 className="text-xl font-black text-[#17091a] mb-8">Typical Outcomes</h3>
              <div className="grid gap-6">
                {industry.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#952877]/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-[#952877]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span className="text-sm font-bold text-[#17091a]/80 uppercase tracking-tight leading-snug">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SOLUTIONS GRID --- */}
      <section className="py-24 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-[#17091a] tracking-tight">What We Help Build</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {industry.solutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-[2.5rem] p-10 border border-gray-100 transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)] hover:-translate-y-2">
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center mb-6 text-[#952877] font-black italic">
                  0{index + 1}
                </div>
                <h3 className="text-2xl font-black text-[#17091a] mb-4">{solution.title}</h3>
                <p className="text-gray-500 font-medium leading-relaxed">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- WORKFLOW SECTION --- */}
      <section className="py-32 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-xs font-black uppercase tracking-[0.3em] text-[#952877] mb-12">The Methodology</h2>
          <div className="relative">
             <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-full bg-gradient-to-b from-[#952877]/20 via-gray-100 to-transparent"></div>
             <div className="space-y-12 relative z-10">
              {industry.howWeWork.split('. ').filter(s => s.trim()).map((sentence, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-4 h-4 rounded-full bg-white border-4 border-[#952877] mb-6 shadow-lg shadow-[#952877]/20"></div>
                  <p className="text-xl md:text-2xl text-[#17091a] font-light leading-relaxed">
                    {sentence.trim()}.
                  </p>
                </div>
              ))}
             </div>
          </div>
        </div>
      </section>

      {/* --- VALUE ADD SECTION --- */}
      <section className="py-24 bg-[#17091a] rounded-t-[4rem] text-white relative overflow-hidden">
         <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#952877]/10 blur-[150px] rounded-full"></div>
         <div className="max-w-7xl mx-auto px-6 relative z-10">
           <h2 className="text-3xl md:text-5xl font-black mb-16 tracking-tighter text-center">Identifying Need</h2>
           <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
             {industry.valueAdd.map((item, index) => (
               <div key={index} className="flex items-start bg-white/5 backdrop-blur-md p-8 rounded-3xl border border-white/10 hover:bg-white/10 transition-colors">
                 <div className="w-2 h-2 rounded-full bg-[#952877] mt-2 mr-4 flex-shrink-0"></div>
                 <p className="text-gray-300 font-light">{item}</p>
               </div>
             ))}
           </div>
         </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section className="py-32 bg-white text-center">
         <div className="max-w-4xl mx-auto px-6">
           <h2 className="text-4xl md:text-6xl font-black text-[#17091a] tracking-tighter mb-10 leading-tight">
             Ready to solve for <br/>
             <span className="bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent italic">{industry.title}?</span>
           </h2>
           <Link to="/contact" className="inline-flex items-center px-10 py-5 bg-[#17091a] text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl hover:bg-[#952877] transition-all duration-500 shadow-xl hover:-translate-y-1">
              Start a Conversation
              <svg className="w-4 h-4 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
           </Link>
         </div>
      </section>

    </div>
  );
};

export default IndustryDetail;